import { DefinitionNode } from "graphql";
import {
  ApolloLink,
  Observable,
  fromError,
  rewriteURIForGET,
  selectURI
} from "@apollo/client";
import {
  serializeFetchParameter,
  defaultPrinter,
  fallbackHttpConfig,
  selectHttpOptionsAndBodyInternal,
  UriFunction,
} from "@apollo/client/link/http";
import {
  handleError,
  readMultipartBody,
  parseAndCheckHttpResponse
} from "@apollo/client/link/http/parseAndCheckHttpResponse";
import {
  getMainDefinition,
  hasDirectives,
  removeClientSetsFromDocument,
  maybe
} from "@apollo/client/utilities";
import { filterOperationVariables } from "@apollo/client/link/utils";
import { invariant } from "@apollo/client/utilities/globals";
import { Printer } from "@apollo/client/link/http/selectHttpOptionsAndBody";

import isExtractableFile from "./isExtractableFile";
import formDataAppendFile from "./formDataAppendFile";
import extractFiles from "./extractFiles";

export interface LinkOptions {
  uri?: string | UriFunction;
  persistedQueries?: boolean;
  includeExtensions?: boolean;
  fetch?: WindowOrWorkerGlobalScope['fetch'];
  headers?: any;
  credentials?: string;
  fetchOptions?: any;
  useGETForQueries?: boolean;
  includeUnusedVariables?: boolean;
  print?: Printer;
  formData?: FormData;
  isExtractableFile?: (value: any) => boolean;
  formDataAppendFile?: (form: FormData, index: string, file: any) => void;
}

const backupFetch = maybe(() => fetch);

export default function createLink({
  uri: fetchUri = "/graphql",
  persistedQueries = false,
  useGETForQueries,
  includeUnusedVariables = false,
  isExtractableFile: customIsExtractableFile = isExtractableFile,
  formDataAppendFile: customFormDataAppendFile = formDataAppendFile,
  print = defaultPrinter,
  fetch: preferredFetch,
  fetchOptions,
  credentials,
  headers,
  includeExtensions,
}: LinkOptions = {}) {
  const linkConfig = {
    http: { includeExtensions },
    options: fetchOptions,
    credentials,
    headers,
  };

  return new ApolloLink((operation) => {
    let chosenURI = selectURI(operation, fetchUri);

    const context = operation.getContext();

    const clientAwarenessHeaders: {
      "apollographql-client-name"?: string;
      "apollographql-client-version"?: string;
    } = {};

    if (context["clientAwareness"]) {
      const { name, version } = context["clientAwareness"];
      if (name) {
        clientAwarenessHeaders["apollographql-client-name"] = name;
      }
      if (version) {
        clientAwarenessHeaders["apollographql-client-version"] = version;
      }
    }

    const contextHeaders = { ...clientAwarenessHeaders, ...context["headers"] };

    const contextConfig = {
      http: context["http"],
      options: context["fetchOptions"],
      credentials: context["credentials"],
      headers: contextHeaders,
    };

    if (hasDirectives(["client"], operation.query)) {
      const transformedQuery = removeClientSetsFromDocument(operation.query);

      if (!transformedQuery) {
        return fromError(
          new Error(
            "HttpLink: Trying to send a client-only query to the server. To send to the server, ensure a non-client field is added to the query or set the `transformOptions.removeClientFields` option to `true`."
          )
        );
      }

      operation.query = transformedQuery;
    }

    //uses fallback, link, and then context to build options
    const { options, body } = selectHttpOptionsAndBodyInternal(
      operation,
      print,
      fallbackHttpConfig,
      linkConfig,
      contextConfig
    );

    if (body.variables && !includeUnusedVariables) {
      body.variables = filterOperationVariables(
        body.variables,
        operation.query
      );
    }

    let controller: AbortController | undefined;
    if (!options["signal"] && typeof AbortController !== "undefined") {
      controller = new AbortController();
      options["signal"] = controller.signal;
    }

    // If requested, set method to GET if there are no mutations.
    const definitionIsMutation = (d: DefinitionNode) => {
      return d.kind === "OperationDefinition" && d.operation === "mutation";
    };
    const definitionIsSubscription = (d: DefinitionNode) => {
      return d.kind === "OperationDefinition" && d.operation === "subscription";
    };
    const isSubscription = definitionIsSubscription(
      getMainDefinition(operation.query)
    );

    if (persistedQueries) {
      var doc = operation.query as any;
      const id = doc['__meta__']?.hash;

      if (id) {
        delete body["query"];
        (body as any)["id"] = id;
      }
    }
    console.log(body);

    const { clone, files } = extractFiles(body, customIsExtractableFile, "");

    if (files.size) {
      if (options.headers)
        delete options.headers["content-type"];

      const form = new FormData();

      form.append("operations", serializeFetchParameter(clone, "Payload"));

      const map: Record<number, any> = {};
      let i = 0;
      files.forEach((paths) => {
        map[++i] = paths;
      });
      form.append("map", JSON.stringify(map));

      i = 0;
      files.forEach((_paths, file) => {
        customFormDataAppendFile(form, String(++i), file);
      });

      options["body"] = form;
    }
    else {
      // does not match custom directives beginning with @defer
      const hasDefer = hasDirectives(["defer"], operation.query);
      if (
        useGETForQueries &&
        !operation.query.definitions.some(definitionIsMutation)
      ) {
        options["method"] = "GET";
      }

      if (hasDefer || isSubscription) {
        options.headers = options.headers || {};
        let acceptHeader = "multipart/mixed;";
        // Omit defer-specific headers if the user attempts to defer a selection
        // set on a subscription and log a warning.
        if (isSubscription && hasDefer) {
          invariant.warn("Multipart-subscriptions do not support @defer");
        }

        if (isSubscription) {
          acceptHeader +=
            "boundary=graphql;subscriptionSpec=1.0,application/json";
        } else if (hasDefer) {
          acceptHeader += "deferSpec=20220824,application/json";
        }
        options.headers["accept"] = acceptHeader;
      }

      if (options["method"] === "GET") {
        const { newURI, parseError } = rewriteURIForGET(chosenURI, body);
        if (parseError) {
          return fromError(parseError);
        }
        chosenURI = newURI;
      } else {
        try {
          (options as any).body = serializeFetchParameter(body, "Payload");
        } catch (parseError) {
          return fromError(parseError);
        }
      }
    }

    return new Observable((observer) => {
      const currentFetch = preferredFetch || maybe(() => fetch) || backupFetch;

      const observerNext = observer.next.bind(observer);
      currentFetch!(chosenURI, options)
        .then((response) => {
          operation.setContext({ response });
          const ctype = response.headers?.get("content-type");

          if (ctype !== null && /^multipart\/mixed/i.test(ctype)) {
            return readMultipartBody(response, observerNext);
          } else {
            return parseAndCheckHttpResponse(operation)(response).then(
              observerNext
            );
          }
        })
        .then(() => {
          controller = undefined;
          observer.complete();
        })
        .catch((err) => {
          controller = undefined;
          handleError(err, observer);
        });

      return () => {
        // XXX support canceling this request
        // https://developers.google.com/web/updates/2017/09/abortable-fetch
        if (controller) controller.abort();
      };
    });
  });
}